// Точка отсчета во всей игре (0, 0) - центр по [x] и низ по [y] дороги из макета (без выхода за пределы макета)

import GameMovementModel from './gameMovementModel';
import gameSettings, {
    getGameCurrentWidthPx, getGameCurrentHeightPx, getIsGamePt
} from '../constants/gameSettings';
import GameMovableObjectsFactory from '../factories/gameMovableObjectsFactory';
import CharacterModel from './characterModel';
import gameObjectTypes from '../constants/gameObjectTypes';
import gameObjectsSettings, { roadSides } from '../constants/gameObjectsSettings/gameObjectsSettings';
import { findIntersection, getRandomInt, getRandomIntInclusive } from '../helpers/mathMethods';
import movableObjectsTypes from '../constants/movableObjectsTypes';
import lootOptions from '../constants/lootOptions';
import RangeModel from './rangeModel';
import CharacterView from '../views/characterView';
import DistanceRangeView from '../views/distanceRangeView';
import { ee } from '../../eventEmitter';
import gameStatuses from '../constants/gameStatuses';
// import endGameStatuses from '../constants/endGameStatuses';
import BackMVC from '../backMVC';
import GameLoggerModel from './gameLoggerModel';
import GameTimerView from '../views/gameTimerView';
import gameLogEvents from '../constants/gameLogEvents';
import gameRegions from '../constants/gameRegions';
import { clamp } from '../helpers/clamp';
import Vector2d from '../helpers/vector2d';

export default class GameModel extends GameMovementModel {
    gameStatus;
    // endGameStatus;
    #score;
    #distanceRange;
    #velocityRange;
    #protectionEnabled;
    #protectionTimeLeft = {};
    #lootObjectsScheme;
    #lootAddingEnabled;

    #gameCurrentWidthPx;
    #gameCurrentHeightPx;
    #gameTimer;
    #isPt;

    #mainCharacter;
    #backMVC;
    #logger;
    #loggerEnabled = true;

    // тик игры
    #tick = 1 / gameSettings.fps;
    #fpsInterval = 1 / gameSettings.fps;
    // пройденное время (замена времени, чтобы не зависеть от реального времени)
    #gameTime;
    #finalDistance;

    // before start variables
    #beforeStartDistance;
    #beforeStartAdditionalDistance;
    #beforeFinishDistance;

    movableObjectsFactory = [];
    movableObjectsAdditionParams = {};

    #mainCharacterMovementVector = null;
    #autoMoveCoordX = null;

    #eventQueue = [];

    #lastUpdateTimestamp = new Date().getTime();

    #activeRegion;
    #activeRegionProgress;

    #currentMovableObjects;
    #movableObjectsByRoadSide;

    #distanceNextObjectCanBeAddedAt;

    #lootObjectsToAddOnce;

    #hideSlowdownEffectsTimeout;
    #hideBlizzardTimeout;

    constructor() {
        super();

        this.#finalDistance = gameSettings.finalDistance * gameSettings.speedOfGame;
        this.#beforeStartDistance = gameSettings.beforeStartDistance;
        this.#beforeStartAdditionalDistance = gameSettings.beforeStartAdditionalDistance;

        this.setStartKinematicsParams({
            startDistance: -this.#beforeStartDistance,
            startVelocity: gameSettings.startVelocity * gameSettings.speedOfGame,
            acceleration: gameSettings.acceleration * gameSettings.speedOfGame,
            velocity: gameSettings.startVelocity * gameSettings.speedOfGame
        });

        this.updateGameSizes();
        this.addResizeListeners();

        this.movableObjectsFactory = new GameMovableObjectsFactory([
            ...movableObjectsTypes,
            gameObjectTypes.LOOT_OBJECT
        ]);

        this.createMainCharacter();
        this.createRanges();
        this.createTimer();
        this.createLog();
    }

    // TODO вынести resize listener в контроллер или наружу
    addResizeListeners() {
        const updateGameSizes = this.updateGameSizes.bind(this);

        window.addEventListener('resize', updateGameSizes);
    }

    createLog() {
        this.#logger = new GameLoggerModel();
    }

    clearLog() {
        this.#logger?.clearLog();
    }

    addLogEvent(type, params) {
        if (!this.#loggerEnabled) return;

        const evenParams = {
            time: this.#gameTime.toFixed(2),
            distance: this.distance.toFixed(2),
            velocity: this.velocity.toFixed(2),
            ...params
        };

        this.#logger.addEvent(type, evenParams);

        // console.log(this.#logger.log);
        // // eslint-disable-next-line no-debugger
        // debugger;
    }

    // setMainCharacterColor() {
    //     this.#mainCharacter.view.setColorOption('red');
    // }

    updateGameSizes() {
        this.#isPt = getIsGamePt();
        this.#gameCurrentWidthPx = getGameCurrentWidthPx();
        this.#gameCurrentHeightPx = getGameCurrentHeightPx();

        if (!this.movableObjectsFactory?.activeObjects) return;

        Object.keys(this.movableObjectsFactory.activeObjects).forEach((objectType) => {
            const activeMovableObjectsByType = this.movableObjectsFactory.activeObjects[objectType];

            activeMovableObjectsByType.forEach((activeObject) => this.updateGameSizesForObject(activeObject));
        });

        this.updateGameSizesForObject(this.#mainCharacter);
        this.#backMVC.update100Vh();
    }

    updateGameSizesForObject(object) {
        if (object.model.updateGameSizes) {
            object.model.updateGameSizes({ width: this.#gameCurrentWidthPx, height: this.#gameCurrentHeightPx });
        }

        if (object.view.updateGameSizes) {
            object.view.updateGameSizes({ width: this.#gameCurrentWidthPx, height: this.#gameCurrentHeightPx });
        }
    }

    setMovableObjectsAdditionParams(objectKey, currentDistance) {
        this.movableObjectsAdditionParams[objectKey] = {
            lastDistance: currentDistance,
            addingPeriod: getRandomInt(
                gameObjectsSettings[objectKey].addingPeriod.from,
                gameObjectsSettings[objectKey].addingPeriod.to
            )
        };
    }

    createMainCharacter() {
        this.#mainCharacter = {
            model: new CharacterModel(),
            view: new CharacterView()
        };

        this.updateGameSizesForObject(this.#mainCharacter);
    }

    init() {
        this.clearLog();

        this.setStartKinematicsParams({
            startDistance: -this.#beforeStartDistance,
            startVelocity: gameSettings.startVelocity * gameSettings.speedOfGame,
            acceleration: gameSettings.acceleration * gameSettings.speedOfGame,
            velocity: gameSettings.startVelocity * gameSettings.speedOfGame
        });
        this.setDefaultGameParams();
        this.setActiveRegion(gameRegions[0]);
        this.clearObjects();
        // this.generateLootableObjectsScheme();
        this.addBeforeStartObjects();
        this.updateCharacterSpeed(0);
        this.updateDistanceRange(0);
        this.updateVelocityRange(this.startVelocity);
        this.resetVelocityModifiers();
        this.resetVisualEffects();
        this.#backMVC.resetAllRegionsProgress(gameRegions);
        // this.#backMVC.hideFinishStyles();
        this.#mainCharacter.view.init();

        this.distance = 0;
        this.#gameTime = 0;
        this.velocity = this.startVelocity;
    }

    play() {
        if (this.gameStatus === gameStatuses.BEFORE_START) {
            this.#mainCharacter.view.start();
            this.#loggerEnabled = true;
            this.addLogEvent(gameLogEvents.GAME_START);
        }

        if (this.gameStatus === gameStatuses.STOP) {
            this.addLogEvent(gameLogEvents.GAME_RESUME);
        }

        this.gameStatus = gameStatuses.PLAY;

        this.#lastUpdateTimestamp = new Date().getTime();

        this.#backMVC.onGameStart();
        this.toUpdate();
    }

    pause() {
        this.gameStatus = gameStatuses.STOP;
        this.addLogEvent(gameLogEvents.GAME_PAUSE);
    }

    setDefaultGameParams() {
        this.#eventQueue = [];
        this.#beforeFinishDistance = gameSettings.beforeFinishDistance * gameSettings.speedOfGame;
        this.#gameTime = 0;
        this.gameStatus = gameStatuses.BEFORE_START;
        // this.endGameStatus = null;
        this.#protectionEnabled = false;
        this.#protectionTimeLeft = {};

        // character
        // const playerY = this.#isPt ? gameSettings.playerYPosPt : gameSettings.playerYPos;

        // this.#mainCharacter.model.setPosition(0, 0);
        this.#mainCharacter.model.updateGameSizes();
        this.#mainCharacter.view.update(0, 0);
        this.stopMovingCharacter();

        // Тут хранятся объекты текущего региона
        this.#movableObjectsByRoadSide = {
            [roadSides.LEFT]: [],
            [roadSides.RIGHT]: []
        };

        // Тут хранится дистанция, на которой может быть добавлен следующий объект
        // Это нужно, чтобы объекты разных типов не накладывались друг на друга
        this.#distanceNextObjectCanBeAddedAt = {
            [roadSides.LEFT]: 0,
            [roadSides.RIGHT]: 0
        };

        // Тут хранятся обхекты, которые нужно добавить 1 раз (на входе в регион)
        // При добвлении у них приоритет перед всеми другими
        this.#lootObjectsToAddOnce = [];
    }

    resetVisualEffects() {
        this.#mainCharacter.view.hideSpeedBoost();
        this.#mainCharacter.view.hideProtection();
        this.#mainCharacter.view.removeAllEffectsDisplay();
    }

    setLevelConfig(config) {
        console.log(config);
        this.#lootObjectsScheme = config;
    }

    // generateLootableObjectsScheme() {
    //     this.#lootObjectsScheme = generateLevelLootableObjects();

    //     console.log(this.#lootObjectsScheme);
    // }

    clearObjects() {
        this.movableObjectsFactory.clearObjects();
        this.movableObjectsAdditionParams = {};
    }

    addBeforeStartObjects() {
        this.addBackground();
        this.addBeforeStartMovableObjects();
    }

    // TODO доработать добавление фона
    addBackground() {
        this.#backMVC = new BackMVC(this.#finalDistance);
    }

    addBeforeStartMovableObjects() {
        this.#currentMovableObjects.forEach((objectKey) => {
            this.setMovableObjectsAdditionParams(objectKey, this.distance);
        });

        this.#lootAddingEnabled = false;

        while (this.distance < this.#beforeStartAdditionalDistance) {
            this.toUpdate(this.#tick, true);
        }

        this.#currentMovableObjects.forEach((objectType) => {
            this.movableObjectsAdditionParams[objectType].lastDistance -= this.distance;
        });

        this.#distanceNextObjectCanBeAddedAt[roadSides.LEFT] -= this.distance;
        this.#distanceNextObjectCanBeAddedAt[roadSides.RIGHT] -= this.distance;

        this.distance = 0;
        this.#gameTime = 0;

        this.toUpdate(this.#tick, true);

        this.#lootAddingEnabled = true;
    }

    createRanges() {
        this.#distanceRange = {
            model: new RangeModel(gameSettings.startDistance, this.#finalDistance),
            view: new DistanceRangeView('distanceRange', gameSettings.startDistance, gameSettings.finalDistanceForView)
        };

        this.#velocityRange = {
            model: new RangeModel(this.minVelocity, this.maxVelocity),
            view: new DistanceRangeView('velocityRange', this.minVelocity, this.maxVelocity)
        };
    }

    createTimer() {
        this.#gameTimer = {
            view: new GameTimerView('gameTimer')
        };
    }

    updateActiveRegion() {
        const activeRegionIndex = gameRegions.indexOf(this.#activeRegion);
        const nextRegion = gameRegions[activeRegionIndex + 1];

        const regionStartDistance = this.#activeRegion.startAtDistance;
        const regionEndDistance = nextRegion?.startAtDistance || this.#finalDistance;
        const currentRegionDistance = this.distance - regionStartDistance;

        const regionProgress = currentRegionDistance / (regionEndDistance - regionStartDistance);

        this.#activeRegionProgress = clamp(regionProgress.toFixed(5), 0, 1);

        if (nextRegion && this.distance > regionEndDistance) {
            this.setActiveRegion(nextRegion);
        }
    }

    setActiveRegion(region) {
        this.#activeRegion = region;
        this.#currentMovableObjects = region.objects;
        this.#activeRegionProgress = 0;

        this.#movableObjectsByRoadSide[roadSides.LEFT] = this.#currentMovableObjects.filter((object) => {
            const objectSettings = gameObjectsSettings[object];

            return !objectSettings.ignoreOtherObjectsSpacing && objectSettings.roadSide === roadSides.LEFT;
        });

        this.#movableObjectsByRoadSide[roadSides.RIGHT] = this.#currentMovableObjects.filter((object) => {
            const objectSettings = gameObjectsSettings[object];

            return !objectSettings.ignoreOtherObjectsSpacing && objectSettings.roadSide === roadSides.RIGHT;
        });

        this.#lootObjectsToAddOnce = [...region.welcomeObjects || []];
    }

    toUpdate(deltaT, skipTimeCheck = false) {
        const timestamp = new Date().getTime();
        const deltaTime = deltaT || (timestamp - this.#lastUpdateTimestamp) / 1000;

        if (deltaTime >= this.#fpsInterval || skipTimeCheck) {
            // console.log(deltaTime * 1000);
            this.update(deltaTime);
            this.#lastUpdateTimestamp = timestamp;
        }

        if (this.gameStatus === gameStatuses.BEFORE_START || this.gameStatus === gameStatuses.STOP) {
            this.updateCharacterSpeed(0);

            return;
        }

        requestAnimationFrame(() => this.toUpdate());
    }

    update(deltaTime) {
        this.#eventQueue.forEach((f) => f());
        this.#eventQueue = [];

        // console.log(this.#mainCharacter.model.position.x);

        const timestamp = new Date().getTime();
        const lastDistance = this.distance;

        if (this.gameStatus === gameStatuses.STOP) {
            this.#lastUpdateTimestamp = timestamp;

            return;
        }

        if (this.gameStatus !== gameStatuses.BEFORE_START && this.gameStatus !== gameStatuses.FINISHING) {
            this.#gameTime += deltaTime;
        }

        this.updateDistanceAndVelocity(deltaTime, this.#gameTime);
        this.updateActiveRegion();
        this.updateProtection(deltaTime);

        const deltaDistance = this.distance - lastDistance;

        this.updateAndShiftMovableObjects(deltaDistance);

        this.toAddIndependentMovableObjects();
        this.toAddRoadSidesMovableObjects();

        if (this.#lootAddingEnabled) {
            this.toAddLootObject();
        }
        this.checkIntersectionLootAndMainCharacter();

        if (this.#mainCharacterMovementVector) {
            this.moveCharacter(deltaTime);
        }

        // if (typeof this.#autoMoveCoordX === 'number') {
        //     this.handleAutoMove();
        // }

        if (this.gameStatus === gameStatuses.FINISHING) return;

        const gameProgress = (this.distance / this.#finalDistance).toFixed(2);

        const backMvcParams = {
            distance: this.distance,
            region: this.#activeRegion,
            regionProgress: this.#activeRegionProgress,
            gameProgress
        };

        this.checkAndSetGameStatus(this.distance);
        this.updateCharacterSpeed();
        this.#backMVC.update(backMvcParams);
        this.updateDistanceRange(this.distance);
        this.updateVelocityRange(this.fullVelocityWithBonus);
        this.#gameTimer.view.update(this.#gameTime);

        if (gameProgress >= gameSettings.progressToShowSalutes && !this.#backMVC.salutesActive) {
            this.#backMVC.showSalutes();
        }
    }

    // handleAutoMove() {
    //     const currentX = this.#mainCharacter.model.position.x;

    //     if (Math.abs(currentX - this.#autoMoveCoordX) <= 0.1) {
    //         this.stopMovingCharacter();
    //         this.#autoMoveCoordX = null;

    //         return;
    //     }

    //     const xDirection = currentX < 0 ? 1 : -1;
    //     const movementVector = new Vector2d(xDirection, 0).unitVector;

    //     this.startMovingCharacter(movementVector);
    // }

    updateAndShiftMovableObjects(deltaDistance) {
        Object.keys(this.movableObjectsFactory.activeObjects).forEach((objectType) => {
            const activeMovableObjectsByType = this.movableObjectsFactory.activeObjects[objectType];

            // update
            activeMovableObjectsByType.forEach((activeObject) => {
                activeObject.model.update(deltaDistance);
                activeObject.view.update(activeObject.model.x, activeObject.model.y, activeObject.model.scale);
            });

            // shift
            // Проверяем только первый элемент, потому что за 1 тик игры добавляется только 1 элемент каждого типа, не чаще.
            if (activeMovableObjectsByType[0]?.model?.isObjectOutOfVisibleArea) {
                this.movableObjectsFactory.shiftObject(objectType);
            }
        });
    }

    toAddLootObject() {
        const firstObjectInList = this.#lootObjectsScheme[0];

        if (!firstObjectInList) return;

        // eslint-disable-next-line no-unused-vars
        const { distance, type, option } = firstObjectInList;

        if (this.distance >= distance) {

            this.addMovableObject(type, {
                option
            });

            // console.log(type, option);

            this.#lootObjectsScheme.shift();

            this.addLogEvent(gameLogEvents.LOOT_ITEM_SPAWN, { lootOption: option });
        }
    }

    toAddRoadSidesMovableObjects() {
        Object.entries(this.#movableObjectsByRoadSide).forEach(([roadSide, roadSideObjectsKeys]) => {

            const firstPriorityObjectKey = this.#lootObjectsToAddOnce[0]?.type;
            const firstPriorityObjectSettings = gameObjectsSettings[firstPriorityObjectKey];

            if (firstPriorityObjectKey && firstPriorityObjectSettings.roadSide === roadSide) {
                const isAdded = this.toAddMovableObjects(firstPriorityObjectKey);

                if (isAdded) {
                    this.#lootObjectsToAddOnce.splice(0, 1);
                }

                return;
            }

            const randomRoadSideObjectKey = roadSideObjectsKeys[getRandomIntInclusive(0, roadSideObjectsKeys.length - 1)];

            this.toAddMovableObjects(randomRoadSideObjectKey);
        });
    }

    toAddIndependentMovableObjects() {
        this.#currentMovableObjects.forEach((objectKey) => {
            if (gameObjectsSettings[objectKey].ignoreOtherObjectsSpacing) {
                this.toAddMovableObjects(objectKey);
            }
        });
    }

    // регулярное добавление объектов в процессе игры
    toAddMovableObjects(objectKey) {
        if (!objectKey) return false;
        const objectSettings = gameObjectsSettings[objectKey];
        const objectAdditionalParams = this.movableObjectsAdditionParams[objectKey];

        const checkRoadSideSpacing = objectSettings.roadSide && !objectSettings.ignoreOtherObjectsSpacing;

        const spacingCheckWithPrevSameObjectPassed = !objectAdditionalParams
            || this.distance - objectAdditionalParams.lastDistance
            >= objectAdditionalParams.addingPeriod;

        const distanceObjectCanBeAddedAt = checkRoadSideSpacing ? this.#distanceNextObjectCanBeAddedAt[objectSettings.roadSide] : 0;
        const spacingCheckWithPrevRoadsideObjectPassed = !checkRoadSideSpacing || this.distance > distanceObjectCanBeAddedAt;

        if (spacingCheckWithPrevSameObjectPassed && spacingCheckWithPrevRoadsideObjectPassed) {
            this.addMovableObject(objectKey);

            this.setMovableObjectsAdditionParams(objectKey, this.distance || 0);

            return true;
        }

        return false;
    }

    addMovableObject(objectKey, options) {
        this.movableObjectsFactory.addObject(
            objectKey,
            {
                ...options
            }
        );

        const { roadSide, spacingAfter } = gameObjectsSettings[objectKey];

        if (roadSide) {
            if (spacingAfter) {
                this.#distanceNextObjectCanBeAddedAt[roadSide] = this.distance + getRandomIntInclusive(spacingAfter.min, spacingAfter.max);
            }
        }

        if (this.movableObjectsFactory.lastAddedObject) {
            this.updateGameSizesForObject(this.movableObjectsFactory.lastAddedObject);
        }
    }

    checkIntersectionLootAndMainCharacter() {
        const iPt = getIsGamePt();

        if (this.movableObjectsFactory.activeObjects[gameObjectTypes.LOOT_OBJECT]?.length) {
            this.movableObjectsFactory.activeObjects[gameObjectTypes.LOOT_OBJECT].forEach((activeLootObject) => {

                const spaceSettings = activeLootObject.model.lootObjectSettings.spaceForIntersection;
                const spaceSettingsPt = activeLootObject.model.lootObjectSettings.spaceForIntersectionPt;
                const objectIntersectionSpace = iPt && spaceSettingsPt ? spaceSettingsPt : spaceSettings;
                const defaultSpaceForIntersection = iPt ? gameSettings.spaceForIntersectionPt : gameSettings.spaceForIntersection;

                const spaceForIntersection = {
                    x: objectIntersectionSpace?.x || defaultSpaceForIntersection,
                    y: objectIntersectionSpace?.y || defaultSpaceForIntersection
                };

                const yPosMod = 1;

                // eslint-disable-next-line max-len
                // console.log(activeLootObject.model.currentYInGame, this.#mainCharacter.model.position.y, this.#mainCharacter.model.position.x);

                if (
                    !activeLootObject.model.hided
                    && activeLootObject.model.currentYInGame + spaceForIntersection.y > 0
                    && activeLootObject.model.currentYInGame + yPosMod <= this.#mainCharacter.model.position.y
                    && findIntersection(
                        [
                            activeLootObject.model.x - spaceForIntersection.x,
                            activeLootObject.model.x + spaceForIntersection.x
                        ],
                        [
                            this.#mainCharacter.model.position.x - defaultSpaceForIntersection,
                            this.#mainCharacter.model.position.x + defaultSpaceForIntersection
                        ]
                    )
                ) {
                    activeLootObject.view.hide();
                    activeLootObject.model.hide();
                    const lootOption = activeLootObject.model.option;

                    switch (lootOption) {
                        case lootOptions.GOOD:
                            this.#eventQueue.push(this.onGoodLootPickup.bind(this));
                            this.addLogEvent(gameLogEvents.LOOT_ITEM_PICKUP, { lootOption });

                            break;
                        case lootOptions.BAD_TREE_LEFT:
                        case lootOptions.BAD_TREE_RIGHT:
                        case lootOptions.BAD_ROADBLOCK:
                        case lootOptions.BAD_ICEBERG:
                        case lootOptions.BAD_ICE:
                        case lootOptions.BAD_SLED:
                        case lootOptions.BAD_LOG:
                            this.#eventQueue.push(this.onBadLootPickup.bind(this, lootOption));
                            this.addLogEvent(gameLogEvents.LOOT_ITEM_PICKUP, { lootOption });

                            break;
                        case lootOptions.PROTECTION_1:
                        case lootOptions.PROTECTION_2:
                        case lootOptions.PROTECTION_3:
                            this.#eventQueue.push(this.enableProtection.bind(this, lootOption));
                            this.addLogEvent(gameLogEvents.LOOT_ITEM_PICKUP, { lootOption });

                            break;
                        default:
                    }
                }
            });
        }
    }

    checkAndSetGameStatus(coveredDistance) {
        if (!this.velocity) return;

        if (coveredDistance >= this.#finalDistance) {
            this.#eventQueue.push(this.finishLevel.bind(this));
        }
    }

    onSpeedBoostEnd() {
        if (!this.hasGoodVelocityModifiers) {
            this.#mainCharacter.view.hideSpeedBoost();
            this.#backMVC.hideSpeedBoostAura();
        }
    }

    onSlowdownEnd() {
        this.#mainCharacter.view.hideSlowdown();
        this.#backMVC.hideSlowdownAura();
    }

    onGoodLootPickup() {
        const endCallback = this.onSpeedBoostEnd.bind(this);
        const velocityBonusValue = gameSettings.goodLootVelocityBonusValue * gameSettings.speedOfGame;

        this.addVelocityModifier(velocityBonusValue, gameSettings.goodLootVelocityBonusDuration, endCallback);

        this.#mainCharacter.view.displaySpeedBoost();
        this.#backMVC.displaySpeedBoostAura();
    }

    onBadLootPickup(id) {
        if (this.#protectionEnabled) return;

        if (this.hasGoodVelocityModifiers) {
            this.resetVelocityModifiers();
            this.onSpeedBoostEnd();
        } else {
            this.addVelocityModifier(gameSettings.badLootVelocityBonusValue, gameSettings.badLootVelocityBonusDuration);
        }

        this.#mainCharacter.view.displaySlowdown();

        clearTimeout(this.#hideSlowdownEffectsTimeout);
        this.#hideSlowdownEffectsTimeout = setTimeout(() => {
            this.onSlowdownEnd();
        }, 2000);

        if (id === lootOptions.BAD_ICEBERG) {
            this.#backMVC.displayBlizzard();

            clearTimeout(this.#hideBlizzardTimeout);
            this.#hideBlizzardTimeout = setTimeout(() => {
                this.#backMVC.hideBlizzard();
            }, gameSettings.blizzardDuration);
        } else {
            this.#backMVC.displaySlowdownAura();
        }
    }

    enableProtection(protectionType) {
        if (!this.#protectionTimeLeft[protectionType]) {
            this.#mainCharacter.view.addEffectDisplay(protectionType);
        }

        this.#protectionTimeLeft[protectionType] = gameSettings.protectionDurations[protectionType];

        this.#protectionEnabled = true;
        this.#mainCharacter.view.displayProtection();
    }

    disableProtection(protectionType) {
        this.#protectionTimeLeft[protectionType] = 0;
        this.#mainCharacter.view.removeEffectDisplay(protectionType);

        const hasAtLeastOneActiveProtection = Object.values(this.#protectionTimeLeft).find((time) => time > 0);

        this.#protectionEnabled = hasAtLeastOneActiveProtection;

        if (!hasAtLeastOneActiveProtection) {
            this.#mainCharacter.view.hideProtection();
        }
    }

    updateProtection(deltaTime) {
        if (!this.#protectionEnabled) return;

        Object.entries(this.#protectionTimeLeft).forEach(([type, time]) => {
            const timeRemaining = time - deltaTime * 1000;

            if (timeRemaining <= 0) {
                this.disableProtection(type);
            } else {
                this.#protectionTimeLeft[type] = timeRemaining;
            }
        });
    }

    startMovingCharacter(movementVector) {
        this.#mainCharacterMovementVector = movementVector;
    }

    stopMovingCharacter() {
        this.#mainCharacterMovementVector = null;
    }

    moveCharacter(deltaTime) {
        const moveSpeedMod = 1.5;
        const refrenceFps = 120;
        const fpsSpeedMod = refrenceFps / gameSettings.fps;

        const offsetValueAtOneStep = Math.sqrt(this.velocity * deltaTime * moveSpeedMod * fpsSpeedMod);

        this.#mainCharacter.model.move(
            this.#mainCharacterMovementVector,
            offsetValueAtOneStep
        );

        const newViewX = this.#mainCharacter.model.position.x - this.#mainCharacter.model.startPoint.x;

        // console.log(this.#mainCharacter.model.position.x, newViewX);

        this.#mainCharacter.view.update(newViewX, 0);
    }

    updateCharacterSpeed(speed) {
        this.#mainCharacter.model.updateSpeedOfSpriteSheet(speed !== undefined ? speed : this.fullVelocityWithBonus);
        this.#mainCharacter.view.updateSpeedOfSpriteSheet(this.#mainCharacter.model.speedOfSpriteSheetSeconds);
    }

    updateDistanceRange(coveredDistance) {
        this.#distanceRange.model.update(coveredDistance);
        this.#distanceRange.view.update(this.#distanceRange.model.percentOfFill);
    }

    updateVelocityRange(velocity) {
        this.#velocityRange.model.update(velocity);
        this.#velocityRange.view.update(this.#velocityRange.model.percentOfFill);
    }

    moveToCenterPos() {
        this.stopMovingCharacter();

        const start = performance.now();
        const duration = 500;
        const intervalStep = 20;

        const currentX = this.#mainCharacter.model.position.x;
        const targetX = 0;
        const totalMoveAmount = targetX - currentX;
        const numSteps = duration / intervalStep;
        const movePerStep = Math.abs(totalMoveAmount / numSteps);

        const xDirection = totalMoveAmount > 0 ? 1 : -1;
        const vector = new Vector2d(xDirection, 0).unitVector;

        let currentStep = 0;
        const interval = setInterval(() => {
            this.#mainCharacter.model.move(vector, movePerStep);
            const newViewX = this.#mainCharacter.model.position.x - this.#mainCharacter.model.startPoint.x;

            this.#mainCharacter.view.update(newViewX, 0);
            currentStep += 1;

            if (currentStep >= numSteps) {
                clearInterval(interval);
                const end = performance.now();

                console.log(`Moving took ${start - end}`);
            }
        }, intervalStep);
    }

    finishLevel() {
        if (this.gameStatus !== gameStatuses.PLAY) return;
        this.addLogEvent(gameLogEvents.GAME_FINISH);

        this.#loggerEnabled = false;
        this.gameStatus = gameStatuses.FINISHING;

        console.log(this.#logger.log);

        ee.emit('gameLevelComplete', {
            time: this.#gameTime, logs: this.#logger.log
        });

        this.moveToCenterPos();
    }

    stopGame() {
        this.gameStatus = gameStatuses.STOP;
        this.#mainCharacter.view.stop();
        this.#backMVC.hideSalutes();
        this.#backMVC.onGameEnd();
    }
}
